// Detalhe da capability (rota /cap/$id). INSPEÇÃO é livre — qualquer capability pode ser vista,
// com ou sem backend. EXECUTAR a ação continua governado: o botão só habilita com READY_BACKEND;
// senão fica desabilitado com motivo honesto (anti-botão-falso preservado, agora no detalhe).
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, getRouteApi } from "@tanstack/react-router";
import { toast } from "sonner";
import { buildActionViews } from "./view-model.js";
import { useCatalog } from "./use-catalog.js";
import { useUiStore } from "./store.js";
import { CapabilityDetailSkeleton } from "@/components/screen-skeletons";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import "./i18n.js";

const routeApi = getRouteApi("/_app/cap/$capabilityId");

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-2 border-b border-border/60 py-2 last:border-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-words">{value}</dd>
    </div>
  );
}

export function CapabilityDetail() {
  const { t } = useTranslation();
  const { capabilityId } = routeApi.useParams();
  const { data: registry, error, isLoading } = useCatalog();
  const recordActivation = useUiStore((s) => s.recordActivation);

  if (isLoading)
    return (
      <main className="mx-auto max-w-2xl p-6">
        <CapabilityDetailSkeleton />
      </main>
    );
  if (error)
    return (
      <main className="p-6 text-sm text-red-400">
        {t("capabilityDetail.catalogError", { message: (error as Error).message })}
      </main>
    );

  const cap = registry?.get(capabilityId) ?? null;
  if (!cap)
    return (
      <main className="mx-auto max-w-2xl space-y-3 p-6">
        <Link to="/control-desk" className="text-sm text-muted-foreground hover:text-foreground">
          ← Control Desk
        </Link>
        <p className="text-sm text-muted-foreground">
          {t("capabilityDetail.unknown", { id: capabilityId })}
        </p>
      </main>
    );

  // view-model dita se a AÇÃO está disponível e o porquê (mesma lógica do grid).
  const view = buildActionViews(registry!).find((v) => v.capabilityId === capabilityId)!;

  return (
    <main className="mx-auto max-w-2xl space-y-4 p-6">
      <Link
        to="/control-desk"
        className="inline-block text-sm text-muted-foreground hover:text-foreground"
      >
        ← Control Desk
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>{cap.title}</CardTitle>
          <div className="flex flex-wrap gap-2 pt-2">
            <Badge variant="outline">{cap.surface_owner}</Badge>
            <Badge variant="secondary">{cap.status}</Badge>
            <Badge variant="outline">{t("capabilityDetail.risk", { level: cap.risk_level })}</Badge>
            {cap.requires_hitl && <Badge>HITL</Badge>}
            {cap.external_ci_required && <Badge>external_ci</Badge>}
          </div>
        </CardHeader>

        <CardContent className="text-sm">
          <dl>
            <Row label="id" value={<code>{cap.id}</code>} />
            <Row label="slug" value={<code className="text-xs">{cap.slug}</code>} />
            <Row label="status" value={cap.status} />
            <Row
              label={t("capabilityDetail.expectedEndpoint")}
              value={
                cap.backend_endpoint_expected ? <code>{cap.backend_endpoint_expected}</code> : "—"
              }
            />
            <Row label={t("capabilityDetail.antiPlaceboRule")} value={cap.anti_placebo_rule} />
          </dl>

          <div className="mt-4 border-t border-border pt-4">
            {view.enabled ? (
              <Button
                onClick={() => {
                  recordActivation(cap.id);
                  toast.success(cap.title, {
                    description: t("capabilityDetail.demoSuccess"),
                  });
                }}
              >
                {t("capabilityDetail.execute")}
              </Button>
            ) : (
              <div className="space-y-1.5">
                <Button disabled variant="outline">
                  {t("capabilityDetail.unavailable")}
                </Button>
                <p className="text-xs text-muted-foreground">
                  {t("capabilityDetail.disabledReason", {
                    reason: view.disabledReason,
                    hint: view.httpHint,
                  })}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </main>
  );
}
